import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  PaginationComponent,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { useCurrentPage } from '@folio/hooks/useCurrentPage';
import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  distributionRequestStatusEnum,
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { useKorintModuleConfig } from 'korint-sdk/react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { DistributionRequestListCreationButton } from './DistributionRequestListCreationButton';
import { DistributionRequestListRow } from './DistributionRequestListRow';
import { DistributionRequestListSendForSignatureButton } from './DistributionRequestListSendForSignatureButton';
import { Group } from '@folio/components/ui/flex';

type DistributionRequestListModuleProps = {
  /**
   * Whether the card is readonly.
   */
  isReadonly?: boolean;

  /**
   * Whether the creation of distribution requests is allowed.
   */
  isCreationDisabled?: boolean;
};

const PAGE_SIZE = 5;

export function DistributionRequestListModule({
  isReadonly = false,
  isCreationDisabled = false,
}: DistributionRequestListModuleProps) {
  const { t } = useTranslation();
  const { brokerageFirmId: paramsBrokerageFirmId } = useAssertedParams();
  const { intermediaryAdditionalInformationFields } = useKorintModuleConfig(
    'distributionRequestListModule',
  );
  const { data: user, status: userStatus } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = paramsBrokerageFirmId ?? user?.brokerageFirmId;

  const currentPage = useCurrentPage('distribution-request-list');

  const { data: distributionRequests, status: listDistributionRequestsStatus } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(brokerageFirmId),
    enabled: !!brokerageFirmId,
  });

  const allDistributionRequests = distributionRequests?.data ?? [];
  const pageDistributionRequests = allDistributionRequests.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const waitingForSignatureDistributionRequestsNumber = allDistributionRequests.filter(
    distributionRequest =>
      distributionRequest.status === distributionRequestStatusEnum.WAITING_FOR_SIGNATURE,
  ).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-row justify-between items-center">
          {t('products.distributedProductsTitle')}
          <Group align="stretch" justify="end" gap={2}>
            {!!waitingForSignatureDistributionRequestsNumber && !isReadonly && (
              <DistributionRequestListSendForSignatureButton
                brokerageFirmId={brokerageFirmId}
                waitingForSignatureDistributionRequestsNumber={
                  waitingForSignatureDistributionRequestsNumber
                }
              />
            )}
            {!isCreationDisabled && (
              <DistributionRequestListCreationButton
                brokerageFirmId={brokerageFirmId}
                title={t('distributionRequests.requestButton')}
              />
            )}
          </Group>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/8">
                {t('distributionRequestList.columns.product')}
              </TableHead>
              <TableHead className="w-2/8">{t('distributionRequestList.columns.status')}</TableHead>
              <TableHead className="w-1/8">
                {t('distributionRequestList.columns.minCommission')}
              </TableHead>
              <TableHead className="w-1/8">
                {t('distributionRequestList.columns.maxCommission')}
              </TableHead>
              <TableHead className="w-1/8">
                {t('distributionRequestList.columns.maxManagementFee')}
              </TableHead>
              <TableHead className="w-2/8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {userStatus === 'pending' || listDistributionRequestsStatus === 'pending' ? (
              <>
                {Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: its the only way to get a unique key
                  <LoadingRow key={`loading-${idx}`} />
                ))}
              </>
            ) : userStatus === 'error' || listDistributionRequestsStatus === 'error' ? (
              <ErrorRow />
            ) : (
              pageDistributionRequests.map(distributionRequest => (
                <DistributionRequestListRow
                  key={distributionRequest.id}
                  distributionRequest={distributionRequest}
                  isReadonly={isReadonly}
                  intermediaryAdditionalInformationFields={intermediaryAdditionalInformationFields}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <PaginationComponent
          totalData={allDistributionRequests.length}
          PAGE_SIZE={PAGE_SIZE}
          isLoading={userStatus === 'pending' || listDistributionRequestsStatus === 'pending'}
          slug="distribution-request-list"
        />
      </CardFooter>
    </Card>
  );
}
